import { useRef } from 'react';
import Slider from 'react-slick';
import img1 from '../../media/coristo.png';
import img2 from '../../media/b&b.png';
import img3 from '../../media/basiaCarrousel.png';
import img4 from '../../media/promyk.png';
import img5 from '../../media/ponchoCarrousel.png';
import img6 from '../../media/p&b.png';
import fieldImg from '../../media/field.jpg';
import {
  ArrowForward,
  ChevronLeft,
  ChevronRight,
  GroupsOutlined,
  HomeOutlined,
  ShieldOutlined,
} from '@mui/icons-material';
import { Box, Button, Divider, Grid, IconButton, Stack, Typography } from '@mui/material';
import { useThemeContext } from '../global/ThemeContext';
import useTranslations from './translation/hooks/translation';
import { designTokens } from '../../theme/designTokens';
import './Carrousel.css';

type CarouselSlide = {
  src: string;
  name: string;
  kind: 'horse' | 'person';
  backdrop: 'arena' | 'blue';
  detail: Record<string, string>;
};

const Carrousel = () => {
  const sliderRef = useRef<Slider | null>(null);
  const { darkMode } = useThemeContext();
  const { currentLanguage, translations } = useTranslations();
  const palette = darkMode ? designTokens.colors.dark : designTokens.colors.light;
  const imgs: CarouselSlide[] = [
    {
      src: img1,
      name: 'Coristo',
      kind: 'horse',
      backdrop: 'arena',
      detail: { en: 'Gelding', pl: 'Wałach' },
    },
    {
      src: img2,
      name: 'Bajka & Balou',
      kind: 'horse',
      backdrop: 'blue',
      detail: { en: 'Mares', pl: 'Klacze' },
    },
    {
      src: img3,
      name: 'Barbara Jimenez',
      kind: 'person',
      backdrop: 'arena',
      detail: { en: 'Coach and show jumper', pl: 'Trenerka i zawodniczka skokowa' },
    },
    {
      src: img4,
      name: 'Promyk',
      kind: 'horse',
      backdrop: 'arena',
      detail: { en: 'Gelding', pl: 'Wałach' },
    },
    {
      src: img5,
      name: 'Alfonso Jimenez',
      kind: 'person',
      backdrop: 'arena',
      detail: { en: 'Tennis player and stable helper', pl: 'Tenisista i pomocnik stajenny' },
    },
    {
      src: img6,
      name: 'Pasja & Frika',
      kind: 'horse',
      backdrop: 'blue',
      detail: { en: 'Mare and pony', pl: 'Klacz i kuc' },
    },
  ];

  const features = [
    { icon: <ShieldOutlined />, label: translations.label.carouselFeatureTraining[currentLanguage] },
    { icon: <GroupsOutlined />, label: translations.label.carouselFeatureAudience[currentLanguage] },
    { icon: <HomeOutlined />, label: translations.label.carouselFeatureYearRound[currentLanguage] },
  ];

  const settings = {
    dots: true,
    infinite: true,
    speed: 650,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6500,
    fade: true,
    arrows: false,
    cssEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
    pauseOnHover: true,
  };

  return (
    <Box
      className={`carousel-root ${darkMode ? 'dark' : 'light'}`}
      sx={{
        bgcolor: palette.background.default,
        p: { xs: 0, md: 2 },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '1700px',
          mx: 'auto',
          overflow: 'hidden',
          borderRadius: { xs: 0, md: designTokens.radius.lg },
        }}
      >
        <Slider ref={sliderRef} {...settings}>
          {imgs.map((img) => (
            <Box key={img.name}>
              <Grid
                container
                sx={{
                  minHeight: { xs: 'auto', md: '650px', xl: '735px' },
                  bgcolor: darkMode ? palette.background.elevated : '#FFFFFF',
                }}
              >
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    position: 'relative',
                    minHeight: { xs: '380px', sm: '450px', md: '650px', xl: '735px' },
                    overflow: 'hidden',
                    bgcolor: img.backdrop === 'blue' ? '#273F7D' : palette.background.subtle,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: img.backdrop === 'arena' ? '-6px' : 0,
                      backgroundImage:
                        img.backdrop === 'arena'
                          ? darkMode
                            ? `linear-gradient(180deg, rgba(9,24,55,0.18), rgba(9,24,55,0.48)), url("${fieldImg}")`
                            : `linear-gradient(180deg, rgba(232,241,252,0.08), rgba(20,56,125,0.16)), url("${fieldImg}")`
                          : darkMode
                            ? 'linear-gradient(145deg, #314B91 0%, #12244F 100%)'
                            : 'linear-gradient(145deg, #3A559A 0%, #20376F 100%)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      filter: img.backdrop === 'arena' ? 'saturate(0.82) contrast(0.96)' : 'none',
                      transform: img.backdrop === 'arena' ? 'scale(1.02)' : 'none',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background:
                        img.backdrop === 'arena'
                          ? darkMode
                            ? 'linear-gradient(90deg, transparent 70%, rgba(9,24,55,0.34) 100%)'
                            : 'linear-gradient(90deg, transparent 70%, rgba(255,255,255,0.30) 100%)'
                          : 'linear-gradient(90deg, transparent 82%, rgba(10,27,61,0.14) 100%)',
                      pointerEvents: 'none',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={img.src}
                    alt={img.name}
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      bottom:
                        img.backdrop === 'blue'
                          ? { xs: '50px', md: '3%' }
                          : { xs: '50px', md: 0 },
                      zIndex: 2,
                      width:
                        img.backdrop === 'blue'
                          ? { xs: '82%', sm: '76%', md: '82%' }
                          : { xs: '96%', sm: '88%', md: '100%' },
                      height:
                        img.backdrop === 'blue'
                          ? { xs: 'calc(88% - 30px)', md: '86%' }
                          : { xs: 'calc(100% - 50px)', md: '100%' },
                      transform: 'translateX(-50%)',
                      objectFit: 'contain',
                      objectPosition: 'center bottom',
                      filter: img.backdrop === 'blue' ? 'none' : 'saturate(0.9)',
                    }}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: darkMode ? palette.background.default : '#FFFFFF',
                    color: palette.text.primary,
                  }}
                >
                  <Box sx={{ width: '100%', p: { xs: 3, sm: 5, md: 6, xl: 8 }, pb: { xs: 4, md: 8 } }}>
                    <Box
                      sx={{
                        display: { xs: 'block', md: 'none' },
                        mb: 2.2,
                        pb: 2,
                        borderBottom: `1px solid ${palette.border.default}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: palette.text.primary }}>
                        {img.name}
                        <Box component="span" sx={{ color: palette.text.secondary, fontWeight: 600 }}>
                          {' | '}{img.detail[currentLanguage]}
                        </Box>
                      </Typography>
                    </Box>
                    <Typography
                      component="p"
                      sx={{
                        color: palette.primary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.22em',
                        fontWeight: 800,
                        fontSize: '0.76rem',
                        mb: 2.5,
                      }}
                    >
                      {translations.label.carouselEyebrow[currentLanguage]}
                    </Typography>
                    <Box sx={{ width: 36, height: 1, bgcolor: palette.border.default, mb: 3 }} />
                    <Typography
                      variant="h1"
                      sx={{
                        color: palette.text.primary,
                        fontSize: { xs: '2.15rem', sm: '3rem', md: 'clamp(3rem, 4vw, 4.7rem)' },
                        lineHeight: 1.03,
                        letterSpacing: '-0.045em',
                        maxWidth: '680px',
                      }}
                    >
                      {translations.label.carouselTitle[currentLanguage]}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: palette.text.secondary,
                        fontSize: { xs: '1rem', md: '1.08rem' },
                        lineHeight: 1.75,
                        maxWidth: '580px',
                        mt: 3,
                      }}
                    >
                      {translations.label.carouselDescription[currentLanguage]}
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                      <Button
                        size="large"
                        variant="contained"
                        href="https://lubie.urbanequestrian.pl/"
                        endIcon={<ArrowForward />}
                        sx={{ px: 3.5, py: 1.55 }}
                      >
                        {translations.label.carouselTrialCta[currentLanguage]}
                      </Button>
                      <Button
                        size="large"
                        variant="outlined"
                        endIcon={<ArrowForward />}
                        onClick={() => document.getElementById('offer')?.scrollIntoView({ behavior: 'smooth' })}
                        sx={{ px: 3.5, py: 1.45, borderColor: palette.text.primary, color: palette.text.primary }}
                      >
                        {translations.label.carouselOfferCta[currentLanguage]}
                      </Button>
                    </Stack>

                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={{ xs: 1, sm: 1.6 }}
                      useFlexGap
                      flexWrap="wrap"
                      sx={{ mt: 4, color: palette.text.secondary }}
                    >
                      {features.map((feature) => (
                        <Stack
                          key={feature.label}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            width: { xs: '100%', sm: 'auto' },
                            minWidth: 0,
                            px: { xs: 1.35, sm: 0 },
                            py: { xs: 1.15, sm: 0 },
                            borderRadius: { xs: designTokens.radius.md, sm: 0 },
                            bgcolor: { xs: darkMode ? palette.background.elevated : '#F4F7FD', sm: 'transparent' },
                          }}
                        >
                          <Box sx={{ display: 'flex', color: palette.text.primary, '& svg': { fontSize: 20 } }}>{feature.icon}</Box>
                          <Typography variant="body2" sx={{ lineHeight: 1.35 }}>{feature.label}</Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                      <Divider sx={{ my: 4, maxWidth: '580px' }} />
                      <Typography variant="caption" sx={{ color: palette.text.secondary, display: 'block' }}>
                        {img.kind === 'horse'
                          ? translations.label.carouselMeetHorses[currentLanguage]
                          : translations.label.carouselMeetTeam[currentLanguage]}
                      </Typography>
                      <Typography variant="h5" sx={{ color: palette.text.primary, mt: 0.4 }}>
                        {img.name}
                        <Box
                          component="span"
                          sx={{ color: palette.text.secondary, fontSize: '0.92rem', fontWeight: 600, letterSpacing: 0 }}
                        >
                          {' | '}{img.detail[currentLanguage]}
                        </Box>
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Slider>

        <IconButton
          aria-label="Previous slide"
          onClick={() => sliderRef.current?.slickPrev()}
          sx={{
            position: 'absolute',
            left: { xs: 12, md: 28 },
            top: { xs: 175, sm: 210, md: '50%' },
            transform: { md: 'translateY(-50%)' },
            zIndex: 3,
            width: { xs: 46, md: 58 },
            height: { xs: 46, md: 58 },
            bgcolor: 'rgba(255,255,255,0.94)',
            color: '#0B1834',
            boxShadow: '0 10px 26px rgba(20,56,125,0.12)',
            '&:hover': { bgcolor: '#FFFFFF', transform: { md: 'translateY(-50%) scale(1.04)' } },
          }}
        >
          <ChevronLeft />
        </IconButton>
        <IconButton
          aria-label="Next slide"
          onClick={() => sliderRef.current?.slickNext()}
          sx={{
            position: 'absolute',
            right: { xs: 12, md: 28 },
            top: { xs: 175, sm: 210, md: '50%' },
            transform: { md: 'translateY(-50%)' },
            zIndex: 3,
            width: { xs: 46, md: 58 },
            height: { xs: 46, md: 58 },
            bgcolor: 'rgba(255,255,255,0.94)',
            color: '#0B1834',
            boxShadow: '0 10px 26px rgba(20,56,125,0.12)',
            '&:hover': { bgcolor: '#FFFFFF', transform: { md: 'translateY(-50%) scale(1.04)' } },
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Carrousel;
